import React, { useEffect, useState } from 'react'
import { useSession } from 'src/hooks/useSession'

import { ToggleButton, Tooltip } from '@s4l/ds'

import { IconHeartFull } from '@s4l/icons/IconHeartFull'
import { IconHeartVoid } from '@s4l/icons/IconHeartVoid'

import { useS4LContext } from '@s4l/react-context'
import { usePage } from 'src/hooks/usePage'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { SECTIONS } from 'src/hooks/useKafkaEvent/constants'
import { useRequireLogin } from '@s4l/utils/hooks/useRequireLogin'

interface LikePropertyButtonProps {
  readonly propertyId: string
  readonly hide?: boolean
}

function LikeButton({ propertyId }: { readonly propertyId: Readonly<string> }) {
  const [isLiked, setIsLiked] = useState(false)
  const { isLogged } = useSession()

  const { domain, i18n } = useS4LContext()

  useEffect(() => {
    domain.GetUserLikesPropertyUseCase.execute({ id: propertyId }).then(([err, like]) => {
      if (err) return setIsLiked(false)
      return setIsLiked(like.isLiked())
    })
  }, [domain, propertyId, isLogged])

  const { pageId } = usePage()
  const { sendEvent: sendClickLikeEvent } = useKafkaEvent({ page: pageId, eventName: 'click' })

  const { callback: handleClick } = useRequireLogin(
    loginStatus => () => {
      if (loginStatus.isUnlogged()) {
        sendClickLikeEvent({ section: SECTIONS.HOTEL_LIKE }, { propertyId, action: 'login' })
        return
      }

      if (isLiked) {
        domain.UnlikeUserPropertyUseCase.execute({ id: propertyId }).then(([error]) => {
          if (!error) setIsLiked(false)
        })
        sendClickLikeEvent({ section: SECTIONS.HOTEL_LIKE }, { propertyId, action: 'unlike' })
      } else {
        domain.LikeUserPropertyUseCase.execute({ id: propertyId }).then(([error]) => {
          if (!error) setIsLiked(true)
        })
        sendClickLikeEvent({ section: SECTIONS.HOTEL_LIKE }, { propertyId, action: 'like' })
      }
    },
    'like-property-button'
  )

  let tooltipText = i18n.formatMessage({ id: 'property-like.tooltip' })

  if (isLogged) {
    tooltipText = isLiked
      ? i18n.formatMessage({ id: 'property-like.tooltip-remove' })
      : i18n.formatMessage({ id: 'property-like.tooltip-logged' })
  }

  return (
    <Tooltip delayClose={0} delayOpen={0}>
      <Tooltip.Trigger>
        <ToggleButton
          key={isLiked ? 'true' : 'false'}
          iconOnActive={<IconHeartFull />}
          iconOnInactive={<IconHeartVoid />}
          activeColor="var(--color-primary-500)"
          animation="heartbeat"
          onClick={handleClick}
          value={isLiked}
        />
      </Tooltip.Trigger>
      <Tooltip.Content animation="fade">{tooltipText}</Tooltip.Content>
    </Tooltip>
  )
}

export default function LikePropertyButton({ propertyId, hide = false }: LikePropertyButtonProps) {
  if (hide) return null
  return <LikeButton propertyId={propertyId} />
}
